import type { ReactNode } from 'react';
import { Paragraph, YStack, type YStackProps } from 'tamagui';

import { SolidSurface } from '@/components/glass-surface';
import { PrimaryButton, type PrimaryButtonProps } from '@/components/primary-button';
import { RingGlyph } from '@/components/ring-glyph';
import { Subheading } from '@/components/subheading';

// Issue #150's highest-value dedup: threads/index.tsx's EmptyThreadsList,
// thread-detail/index.tsx's PostsListStatus, matches/index.tsx's
// EmptyMatchesList, profile-view/index.tsx's profileNotFound/error
// branches, and reveal-requests/index.tsx's firstLoadFailed branch had
// all independently hand-rolled the exact same shape (confirmed by
// reading all five before writing this, per this task's own
// instruction): a centered YStack, a Subheading-tier title, one or more
// Paragraph lines of body copy, and an optional PrimaryButton retry/CTA
// -- the ONLY real differences between a "true empty" render (no
// action) and a "load failed" render (title/copy imply failure, action
// is a retry) were the strings and whether `action` was passed, never
// the structure. That's why this is one component with props, not two.
//
// Deliberately does NOT own the copy -- every real call site above had
// its own specific, already-reviewed messaging (issue #123's "feedback
// may not outrun the truth" fixes, #116's reveal-status copy tightening
// twice), so genericizing it here would silently undo that review work.
// `title`/`description` are required inputs, not defaults.
export interface EmptyStateAction {
  label: ReactNode;
  onPress: () => void;
  // Mirrors PrimaryButton's own size variants -- PostsListStatus's
  // retry (a reply-list footer, not a full-screen state) already used
  // "sm" before this component existed; every full-screen empty/error
  // block used the default "md". Exposed here instead of hardcoding
  // "md" so that real distinction survives the migration.
  size?: PrimaryButtonProps['size'];
}

export interface EmptyStateProps extends YStackProps {
  title: string;
  // string | string[] -- EmptyMatchesList (matches/index.tsx) is a real
  // call site with two separate Paragraph lines (what a match is, then
  // how to get one), not one long paragraph; an array renders one
  // Paragraph per entry instead of forcing that call site to hand-join
  // them with a linebreak.
  description?: string | string[];
  // Optional retry/CTA slot -- present on every "load failed" call site
  // (Try again -> refetch()) and on one true-empty call site
  // (threads/index.tsx's needsProfile branch -> Create profile), absent
  // on the rest (EmptyThreadsList, EmptyMatchesList, PostsListStatus's
  // empty branch, profile-view's not-found branch have nothing to
  // retry). No `loading` flag on this type -- grepped every real retry
  // button before designing this (threads/matches/profile-view/
  // thread-detail/reveal-requests all call PrimaryButton with a plain
  // label, never an ActivityIndicator swap on retry) -- adding a loading
  // state here would be inventing a state none of today's real usage
  // has, which is exactly what Card's own "don't invent states a
  // primitive has no use for" precedent warns against.
  action?: EmptyStateAction;
  // Optional icon slot -- a bare icon (e.g. this file's own convention:
  // an inline lucide-style SVG, `size` unset so it fills its badge).
  // EmptyState wraps whatever is passed in a fixed 72px circular
  // SolidSurface badge -- this used to be an unstyled passthrough (no
  // real call site used it), which left this component's actual
  // headline visual -- the thing five different "nothing here yet"
  // screens across the app all render through -- as plain text with a
  // gap where a real system motif could sit. The solid badge is this
  // app's own card material (SolidSurface); using it here amplifies
  // a genuinely flat, high-traffic moment with the system's own
  // strongest move instead of inventing a new one for this component.
  icon?: ReactNode;
  // 72 (full-screen states) is too large for the two compact call
  // sites that render inside an already-scrolling list (PostsListStatus's
  // reply-list header) rather than owning the whole viewport -- 48
  // keeps the badge proportional to that tighter context instead of
  // overpowering a two-line status row.
  iconSize?: number;
  // The 96px distance-ring illustration: the "nothing here yet" mark for true
  // empty states. Errors keep the badge + icon above.
  ring?: boolean;
}

// flex 1 + centered is the majority shape (5 of 7 real call sites this
// task migrates: threads' needsProfile/error, thread-detail's
// threadNotFound/error, profile-view's not-found/error, matches' error,
// reveal-requests' firstLoadFailed -- these are all full-screen swaps
// with nothing else on screen). The two call sites that AREN'T full-
// screen (EmptyThreadsList and EmptyMatchesList, both a FlatList's
// ListEmptyComponent sitting inside an already-scrollable list, and
// PostsListStatus, a FlatList's ListHeaderComponent) override flex/
// justifyContent/gap/padding explicitly via this component's own
// YStackProps passthrough -- spreading `props` AFTER these defaults
// below, so any prop the caller passes always wins, exactly like
// Card's own cardStyle/hoverStyle/pressStyle merge order.
const DEFAULT_CONTAINER = {
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
  gap: '$3',
  px: '$6',
} as const satisfies YStackProps;

export function EmptyState({ title, description, action, icon, ring, iconSize = 72, ...props }: EmptyStateProps) {
  const paragraphs = description === undefined ? [] : Array.isArray(description) ? description : [description];

  return (
    <YStack {...DEFAULT_CONTAINER} {...props}>
      {ring && <RingGlyph size={96} />}
      {icon && !ring && (
        <SolidSurface
          borderRadius="$full"
          style={{ width: iconSize, height: iconSize, alignItems: 'center', justifyContent: 'center' }}
        >
          {icon}
        </SolidSurface>
      )}
      <Subheading color="$color" textAlign="center">
        {title}
      </Subheading>
      {paragraphs.map((line) => (
        // Keyed on the line itself, not an index -- these are static,
        // caller-supplied copy arrays (never reordered/filtered at
        // runtime), so the text is already a stable, unique key without
        // introducing an index-key footgun for a list that could change.
        <Paragraph key={line} color="$colorSecondary" textAlign="center">
          {line}
        </Paragraph>
      ))}
      {action && (
        <PrimaryButton size={action.size} onPress={action.onPress}>
          {action.label}
        </PrimaryButton>
      )}
    </YStack>
  );
}
